import { apiFetch } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import type {
  ApproveCreateProductRequestPayload,
  ApproveMapProductRequestPayload,
  CreateProductRequestPayload,
  ProductRequest,
  ProductRequestPage,
  ProductRequestPageQuery,
  RejectProductRequestPayload,
} from "./types";

export function toBackendProductRequestUnit(
  unit: ApproveCreateProductRequestPayload["default_unit"],
) {
  switch (unit) {
    case "kg":
      return "KG";
    case "cx_un":
      return "CAIXA";
    case "bdj":
      return "BDJ";
    case "un":
      return "UNIDADE";
    case "sc_kg":
    case "cx_kg":
    default:
      return "KG";
  }
}

export function listSupplierProductRequests(
  limit = 2000,
  offset = 0,
): Promise<ProductRequestPage> {
  return apiFetch(
    `${ENDPOINTS.productRequests.supplierList}?limit=${limit}&offset=${offset}`,
  );
}

function productRequestPageParams(
  query: ProductRequestPageQuery,
  includeStatus: boolean,
  includeRequestId: boolean,
) {
  const params = new URLSearchParams({
    limit: String(query.limit),
    offset: String(query.offset),
  });
  const search = query.search.trim();
  const date = query.date?.trim();
  const requestId = query.requestId?.trim();
  if (search) params.set("search", search);
  if (includeStatus && query.status) params.set("status", query.status);
  if (date) params.set("date", date);
  if (includeRequestId && requestId) params.set("request_id", requestId);
  return params;
}

export function listSupplierProductRequestsPage(
  query: ProductRequestPageQuery,
  signal?: AbortSignal,
): Promise<ProductRequestPage> {
  const params = productRequestPageParams(query, true, false);
  return apiFetch(`${ENDPOINTS.productRequests.supplierList}?${params}`, {
    signal,
  });
}

export function createSupplierProductRequest(
  payload: CreateProductRequestPayload,
): Promise<ProductRequest> {
  return apiFetch(ENDPOINTS.productRequests.supplierList, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listPendingProductRequests(
  limit = 2000,
  offset = 0,
): Promise<ProductRequestPage> {
  return apiFetch(
    `${ENDPOINTS.productRequests.pending}?limit=${limit}&offset=${offset}`,
  );
}

export function listPendingProductRequestsPage(
  query: ProductRequestPageQuery,
  signal?: AbortSignal,
): Promise<ProductRequestPage> {
  const params = productRequestPageParams(query, false, true);
  return apiFetch(`${ENDPOINTS.productRequests.pending}?${params}`, { signal });
}

export function approveCreateProductRequest(
  id: string,
  payload: ApproveCreateProductRequestPayload,
): Promise<ProductRequest> {
  return apiFetch(ENDPOINTS.productRequests.approveCreate(id), {
    method: "POST",
    body: JSON.stringify({
      ...payload,
      default_unit: toBackendProductRequestUnit(payload.default_unit),
    }),
  });
}

export function approveMapProductRequest(
  id: string,
  payload: ApproveMapProductRequestPayload,
): Promise<ProductRequest> {
  return apiFetch(ENDPOINTS.productRequests.approveMap(id), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function rejectProductRequest(
  id: string,
  payload: RejectProductRequestPayload,
): Promise<ProductRequest> {
  return apiFetch(ENDPOINTS.productRequests.reject(id), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
